import { useField, useFormikContext } from 'formik';
import { DateTime } from 'luxon';

import React, { useEffect, useState } from 'react';
import { formatDateToUTC, isDateValid, parseDateUTC } from '../../../utils';
import SummaryDisplay from '../../SummaryDisplay';
import CustomDatePicker from './CustomDatePicker';
import { DatePickerProps } from './types';

const DatePicker = (datePickerProps: DatePickerProps) => {
    const {
        calendarButtonTitle,
        containerClassName,
        id,
        inlineHelp,
        isSummary,
        label,
        name,
        placeholder,
        startDate,
        ...theRest
    } = datePickerProps;

    const [_field, _meta] = useField(name);
    const { setFieldValue, setFieldTouched } = useFormikContext();
    const [currentDate, setCurrentDate] = useState<Date | null>(null);

    useEffect(() => {
        if (!currentDate && _field.value) {
            const parsedDate = parseDateUTC(_field.value);
            if (parsedDate) {
                setCurrentDate(parsedDate);
            }
        }
    }, [_field.value, currentDate]);

    const dateOnChange = async (dateUpdateValue: Date | string | null) => {
        let dateString: string | null;

        if (dateUpdateValue === null) {
            dateString = null;
        } else if (typeof dateUpdateValue === 'string') {
            if (isDateValid(dateUpdateValue)) {
                dateString = formatDateToUTC(dateUpdateValue);
            } else {
                dateString = dateUpdateValue;
            }
        } else {
            dateString = formatDateToUTC(dateUpdateValue);
        }

        await setFieldValue(_field.name, dateString, true);
    };

    const dateOnBlur = async (event: React.FocusEvent) => {
        const element = (event.target as HTMLInputElement);
        const dateUpdateValue = element.value;

        if (dateUpdateValue !== _field.value) {
            await dateOnChange(dateUpdateValue || null);
        }

        setFieldTouched(_field.name);
    };

    const getDateTime = (date: string | Date) => {
        if (typeof date === 'string') {
            const dateFromIso = DateTime.fromISO(date);
            return DateTime.fromObject(
                { year: dateFromIso.year, month: dateFromIso.month, day: dateFromIso.day },
            );
        }
        return DateTime.fromObject(
            { year: date.getFullYear(), month: date.getMonth(), day: date.getDay() },
        );
    };

    if (isSummary) {
        const displayValue = _field.value ? getDateTime(_field.value).toFormat('dd LLL yyyy') : _field.value;

        return (
            <SummaryDisplay
                as='p'
                id={id || name}
                label={label}
                value={displayValue}
            />
        );
    }

    return (
        <CustomDatePicker
            {...theRest}
            calendarButtonTitle={calendarButtonTitle}
            containerClassName={`custom-date-picker-div ${containerClassName || ''}`}
            currentDate={currentDate}
            dateOnBlur={dateOnBlur}
            dateOnChange={dateOnChange}
            errorMessage={_meta.error}
            hasError={!!(_meta.touched && _meta.error)}
            id={id}
            inlineHelp={inlineHelp}
            name={name}
            label={label}
            placeholder={placeholder || 'dd/mm/yyyy'}
        />
    );
};

export default DatePicker;
